import { useState } from "react";
import {
  Reclamo,
  Caso,
  Holding,
  Usuario,
  EventoEstado,
  Documento,
  EstadoReclamo,
} from "../../types";
import { Badge, getEstadoReclamoBadgeVariant } from "../common/Badge";

interface ReclamoDetailProps {
  reclamo: Reclamo;
  caso?: Caso;
  holding?: Holding;
  usuarios: Usuario[];
  eventosEstado: EventoEstado[];
  documentos: Documento[];
  usuarioActual: Usuario;
  onReclamoUpdate: (reclamo: Reclamo) => void;
  onEventoEstadoCreate: (data: Omit<EventoEstado, "id">) => number;
  onDocumentoCreate: (data: Omit<Documento, "id" | "fechaSubida">) => void;
}

const STEPPER: { estado: EstadoReclamo; label: string }[] = [
  { estado: "preparacion", label: "Preparacion" },
  { estado: "presentado", label: "Presentado" },
  { estado: "en_tramite", label: "En Tramite" },
  { estado: "resuelto", label: "Resuelto" },
];

const TIPO_LABELS: Record<string, string> = {
  reposicion_sii: "Reposicion SII",
  tta: "TTA",
  corte_apelaciones: "Corte de Apelaciones",
};

export function ReclamoDetail({
  reclamo,
  caso,
  holding,
  usuarios,
  eventosEstado,
  usuarioActual,
  onReclamoUpdate,
  onEventoEstadoCreate,
}: ReclamoDetailProps) {
  const [activeTab, setActiveTab] = useState<"info" | "progreso">("info");
  const [showCambioEstado, setShowCambioEstado] = useState(false);
  const [comentarioCambio, setComentarioCambio] = useState("");
  const [nuevoEstado, setNuevoEstado] = useState<EstadoReclamo | "">("");

  const currentStepIdx = STEPPER.findIndex((s) => s.estado === reclamo.estado);
  const isResolved = reclamo.estado === "resuelto" || reclamo.estado === "desistido";

  const getNextStates = (): EstadoReclamo[] => {
    if (isResolved) return [];
    const next: EstadoReclamo[] = [];
    const idx = currentStepIdx;
    if (idx < STEPPER.length - 1) next.push(STEPPER[idx + 1].estado);
    if (reclamo.estado !== "desistido") next.push("desistido");
    return next;
  };

  const handleCambioEstado = () => {
    if (!nuevoEstado || !comentarioCambio.trim()) return;
    const now = new Date().toISOString().split("T")[0];

    onEventoEstadoCreate({
      entidadTipo: "reclamo",
      entidadId: reclamo.id,
      estadoAnterior: reclamo.estado,
      estadoNuevo: nuevoEstado,
      comentario: comentarioCambio.trim(),
      usuarioId: usuarioActual.id,
      fecha: now,
    });

    let resultado = reclamo.resultado;
    if (nuevoEstado === "resuelto") {
      resultado = "ganado"; // Default, can be changed
    }

    onReclamoUpdate({
      ...reclamo,
      estado: nuevoEstado,
      resultado,
      fechaActualizacion: now,
      fechaResolucion: nuevoEstado === "resuelto" ? now : reclamo.fechaResolucion,
    });

    setShowCambioEstado(false);
    setComentarioCambio("");
    setNuevoEstado("");
  };

  const getUsuarioNombre = (id?: number) =>
    id ? usuarios.find((u) => u.id === id)?.nombre || "-" : "-";

  const tabs = [
    { id: "info" as const, label: "Info" },
    { id: "progreso" as const, label: "Progreso" },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-white">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-slate-800">{reclamo.titulo}</h2>
          <Badge variant={getEstadoReclamoBadgeVariant(reclamo.estado)}>
            {reclamo.estado.replace("_", " ")}
          </Badge>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 mt-3">
          {STEPPER.map((step, idx) => {
            const isCurrent = step.estado === reclamo.estado;
            const isPast = idx < currentStepIdx;
            return (
              <div key={step.estado} className="flex items-center gap-1">
                {idx > 0 && (
                  <div className={`w-6 h-0.5 ${isPast ? "bg-green-400" : "bg-slate-200"}`} />
                )}
                <div
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    isCurrent
                      ? "bg-blue-100 text-blue-700 ring-1 ring-blue-400"
                      : isPast
                      ? "bg-green-100 text-green-700"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {step.label}
                </div>
              </div>
            );
          })}
          {reclamo.estado === "desistido" && (
            <div className="flex items-center gap-1">
              <div className="w-6 h-0.5 bg-red-300" />
              <div className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700 ring-1 ring-red-400">
                Desistido
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        {!isResolved && (
          <div className="flex gap-2 mt-3">
            {getNextStates().map((estado) => (
              <button
                key={estado}
                onClick={() => {
                  setNuevoEstado(estado);
                  setShowCambioEstado(true);
                }}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                  estado === "desistido"
                    ? "bg-red-100 text-red-700 hover:bg-red-200"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {estado === "desistido" ? "Desistir" : `Avanzar a ${estado.replace("_", " ")}`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cambio estado modal */}
      {showCambioEstado && (
        <div className="p-4 bg-yellow-50 border-b border-yellow-200">
          <p className="text-sm font-medium text-yellow-800 mb-2">
            Cambiar estado a: <strong>{nuevoEstado?.replace("_", " ")}</strong>
          </p>
          <textarea
            value={comentarioCambio}
            onChange={(e) => setComentarioCambio(e.target.value)}
            placeholder="Comentario obligatorio..."
            rows={2}
            className="w-full px-3 py-1.5 border border-yellow-300 rounded-lg text-sm mb-2"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowCambioEstado(false)}
              className="text-xs px-3 py-1.5 text-slate-600"
            >
              Cancelar
            </button>
            <button
              onClick={handleCambioEstado}
              disabled={!comentarioCambio.trim()}
              className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg disabled:bg-slate-300"
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white px-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${
              activeTab === tab.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === "info" && (
          <div className="space-y-4">
            {/* Datos de Origen */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Datos de Origen</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Solicitud:</span>
                  <span className="ml-2 text-slate-800">{caso?.titulo || "-"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Holding:</span>
                  <span className="ml-2 text-slate-800">{holding?.nombre || "-"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Tipo Reclamo:</span>
                  <span className="ml-2 text-slate-800">{TIPO_LABELS[reclamo.tipoReclamo] || reclamo.tipoReclamo}</span>
                </div>
                <div>
                  <span className="text-slate-500">Ejecutivo:</span>
                  <span className="ml-2 text-slate-800">{getUsuarioNombre(reclamo.ejecutivoId)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Abogado:</span>
                  <span className="ml-2 text-slate-800">{getUsuarioNombre(reclamo.abogadoId)}</span>
                </div>
              </div>
            </div>

            {/* Motivo */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Motivo del Reclamo</h3>
              <p className="text-sm text-slate-600">{reclamo.motivoReclamo}</p>
            </div>

            {/* Resultado (si resuelto) */}
            {reclamo.resultado && (
              <div className={`p-3 rounded-lg text-sm ${
                reclamo.resultado === "ganado" ? "bg-green-50 text-green-700" :
                reclamo.resultado === "perdido" ? "bg-red-50 text-red-700" :
                "bg-yellow-50 text-yellow-700"
              }`}>
                Resultado: <strong>{reclamo.resultado}</strong>
                {reclamo.fechaResolucion && (
                  <span className="ml-2 opacity-70">
                    ({new Date(reclamo.fechaResolucion).toLocaleDateString("es-CL")})
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "progreso" && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Timeline de Eventos</h3>
            {eventosEstado.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No hay eventos registrados</p>
            ) : (
              <div className="space-y-2">
                {[...eventosEstado]
                  .sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime())
                  .map((evento) => (
                    <div key={evento.id} className="bg-white rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={getEstadoReclamoBadgeVariant(evento.estadoNuevo)}>
                          {evento.estadoNuevo.replace("_", " ")}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          desde {evento.estadoAnterior.replace("_", " ")}
                        </span>
                        <span className="text-xs text-slate-400 ml-auto">
                          {new Date(evento.fecha).toLocaleDateString("es-CL")}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{evento.comentario}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Por {getUsuarioNombre(evento.usuarioId)}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
